import {
	Checkbox,
	MenuItem,
	Stack,
	ListItemText,
	Typography,
} from '@mui/material';

import { useSelector } from 'react-redux';

import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { Loader } from 'common/components/Loader/Loader';
import Tooltip from 'common/components/Tooltip/Tooltip';
import { MODULE_NAME } from 'common/constants';
import { RELATION_MAPPING_TAB_VALUES } from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IAddMappingMenu } from '../../api/type-def';
import { fetchCurrentEntityData } from '../../TaxonomyUtils';

import styles from '../AddMappingModal/AddMappingModal.module.scss';

const { checkboxcheckedcolor, checkboxuncheckedcolor } = styles;
const { OBLIGATIONS } = RELATION_MAPPING_TAB_VALUES;
const { RISK_REFERENCE_TAXONOMY_NAME, REQUIREMENT_INVENTORY_NAME } =
	MODULE_NAME;

const RenderRow = ({
	index,
	style,
	data,
	selectedOptions,
	tabValue,
	handleOptionChange,
	moduleType,
}: ListChildComponentProps<any[]> & { selectedOptions: any[] } & {
	tabValue: string;
} & { handleOptionChange: (option: any) => void } & {
	moduleType: string | undefined;
}) => {
	const item = data[index];

	const getDisplayName = () =>
		item?.displayId
			? `${toSentenceCase(item?.displayId)} | ${toSentenceCase(item?.name)}`
			: toSentenceCase(item?.name);

	return (
		<Stack
			className={styles['add-mapping-menu-container']}
			key={item?.id}
			style={style}
		>
			<MenuItem
				sx={{
					padding: 0,
					cursor: 'default',
					width: '100%',
				}}
			>
				{!(
					tabValue?.toLowerCase() === OBLIGATIONS.toLowerCase() &&
					(moduleType?.toLowerCase() ===
						RISK_REFERENCE_TAXONOMY_NAME?.toLowerCase() ||
						moduleType?.toLowerCase() ===
							REQUIREMENT_INVENTORY_NAME.toLowerCase())
				) && (
					<Checkbox
						sx={{
							color: checkboxuncheckedcolor,
							'&.Mui-checked': { color: checkboxcheckedcolor },
							'&.MuiCheckbox-indeterminate': {
								color: checkboxcheckedcolor,
							},
						}}
						checked={selectedOptions?.some(
							(selectedOption) => selectedOption?.id === item?.id,
						)}
						onChange={() => handleOptionChange(item)}
						disableRipple
						disabled={item?.mapped}
					/>
				)}
				<Tooltip
					title={
						tabValue?.toLowerCase() !== OBLIGATIONS.toLowerCase()
							? getDisplayName()
							: toSentenceCase(item?.groupName)
					}
					arrow
				>
					<ListItemText
						className={styles['item-label']}
						primary={
							<span className={styles['truncate-text']}>
								{tabValue?.toLowerCase() !== OBLIGATIONS.toLowerCase()
									? getDisplayName()
									: toSentenceCase(item?.groupName)}
							</span>
						}
					/>
				</Tooltip>
			</MenuItem>
		</Stack>
	);
};

const AddMappingMenuItem = (props: IAddMappingMenu) => {
	/* Descructuring variables */
	const {
		selectedOptions,
		tabValue,
		isEntityDataLoading,
		isAddingRelationMapping,
		handleOptionChange,
		handleSelectAllChange,
		moduleType,
	} = props;

	/* Store Variables */
	const entityResponseData = useSelector(
		(state: RootState) => state.taxonomy.entityResponseData,
	);

	const allEntityResponseData = useSelector(
		(state: RootState) => state.taxonomy.allEntityResponseData,
	);

	/* To fetch the entity data based on tab */
	const entityData = fetchCurrentEntityData(
		tabValue,
		allEntityResponseData,
		entityResponseData,
	);

	const sortedEntityData = [
		...entityData.filter((entity: any) => !entity.mapped),
		...entityData.filter((entity: any) => entity.mapped),
	];

	return (
		<div className={styles['add-mapping-section']}>
			{isEntityDataLoading || isAddingRelationMapping ? (
				<Loader show className={styles['loader-style']} />
			) : (
				<div>
					{entityData?.length > 0 ? (
						<div>
							<MenuItem
								sx={{
									padding: 0,
									width: '100%',
									cursor: 'default',
								}}
							>
								{!(
									tabValue?.toLowerCase() === OBLIGATIONS.toLowerCase() &&
									(moduleType?.toLowerCase() ===
										RISK_REFERENCE_TAXONOMY_NAME?.toLowerCase() ||
										moduleType?.toLowerCase() ===
											REQUIREMENT_INVENTORY_NAME.toLowerCase())
								) && (
									<>
										<Checkbox
											sx={{
												color: checkboxuncheckedcolor,
												'&.Mui-checked': { color: checkboxcheckedcolor },
												'&.MuiCheckbox-indeterminate': {
													color: checkboxcheckedcolor,
												},
											}}
											checked={false}
											onChange={handleSelectAllChange}
											disableRipple
											data-testid={`checkbox-${tabValue}`}
											disabled
										/>
										<Tooltip title="Select all" arrow>
											<ListItemText
												className={styles['item-label']}
												primary={toSentenceCase('Select all')}
											/>
										</Tooltip>
									</>
								)}
							</MenuItem>
							<List
								height={280}
								itemCount={sortedEntityData?.length}
								itemSize={35}
								width="100%"
								itemData={sortedEntityData}
							>
								{(listProps) => (
									<RenderRow
										{...listProps}
										selectedOptions={selectedOptions}
										tabValue={tabValue}
										handleOptionChange={handleOptionChange}
										moduleType={moduleType}
									/>
								)}
							</List>
						</div>
					) : (
						<div>
							<Typography className={styles['no-data-wrapper']}>
								{toSentenceCase('No data found')}
							</Typography>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AddMappingMenuItem;
